import React, { PropsWithChildren } from 'react';
import { Box, Container, SxProps } from '@mui/material';
import LoadingLottie from '@/components/LoadingLottie';
import useLayoutSize from '@/hooks/useLayoutSize';
import { Theme } from '@mui/material/styles';
import { NotificationZIndex } from '@/constant/z-index';
import { useRouter } from 'next/router';
import { LayoutType } from '@/constant/layout';
import NewNotificationsPopup from '@/components/@IntranetLibrary/Notification/NewNotificationsPopup';
import { useNotificationTopics } from '@/hooks/useNotificationTopics';
import { AuthContext } from '@/contexts/AuthContext';
import { createContextFactory } from '@/contexts/createContextFactory';
import GNB from '@/components/@IntranetLibrary/GNB';
import { GNBHeight } from '@/components/@IntranetLibrary/GNB/styles';

type DefaultLayoutProps = PropsWithChildren<{
  layoutType?: LayoutType;
  pageBodyColor?: string;
  containerSx?: SxProps<Theme>;
  isShowTabs?: boolean;
  /** 앱 웹뷰처럼 호스트가 자체 chrome 을 갖는 경우 GNB/탭 제거. embedded 쿼리와 동일 효과 */
  hideChrome?: boolean;
}>;

export const IframeFlag = 'embedded';

const DefaultLayout = ({
  children,
  layoutType = LayoutType.DEFAULT,
  pageBodyColor = '#f3f5f6',
  containerSx,
  isShowTabs,
  hideChrome = false,
}: DefaultLayoutProps) => {
  const { isMobile, maxDesktopWidth } = useLayoutSize(layoutType);
  const { isInitialized } = createContextFactory(AuthContext);

  const router = useRouter();
  const isEmbedded = Boolean(router.query[IframeFlag]);
  const isGNBHidden = isEmbedded || hideChrome;

  //Render 전 호출시 장애발생로 수정. 회의록 개별 페이지에서 발생
  useNotificationTopics();

  if (!isInitialized) {
    // 초기화 중 빈 화면은 호스트가 로딩을 그리는 iframe 임베드 한정 — 앱 웹뷰는 스피너 유지
    return isEmbedded ? null : <LoadingLottie opacity={1} loop={true} />;
  }

  return (
    // 앱 웹뷰 다크 모드에서는 페이지 바탕을 다크로 (라이트는 받은 색 그대로 — styles/appDarkMode.ts)
    <Box sx={{ backgroundColor: `var(--dk-bg, ${pageBodyColor})` }}>
      <Container
        sx={{
          '--page-max-width': `${maxDesktopWidth}px`,
          '--gnb-height': isGNBHidden ? '0px' : GNBHeight,
          minWidth: '360px',
          boxSizing: 'border-box',
          paddingTop: 'var(--gnb-height)',
          paddingX: '0 !important', // Container 기본 padding 있어 !important 처리
          maxWidth: 'var(--page-max-width) !important',
          ...containerSx,
        }}
      >
        {!isGNBHidden && <GNB isShowTabs={isShowTabs} />}

        {children}

        {/* 알림 팝업 */}
        <NewNotificationsPopup
          sx={{
            position: 'absolute',
            top: isGNBHidden ? 0 : `calc(1px + ${GNBHeight})`,
            right: isGNBHidden ? 0 : isMobile ? '10px' : '30px',
            zIndex: NotificationZIndex,
          }}
        />
      </Container>
    </Box>
  );
};

export default DefaultLayout;
